import { Component, computed, inject, input, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { FileTypeEnum } from '@api/model/fileTypeEnum';
import { ManagementCompaniesGetParameters } from '@api/model/managementCompaniesGetParameters';
import { ManagementCompany } from '@api/model/managementCompany';
import { ManagementOrganisation } from '@api/model/managementOrganisation';
import { ManagementSessionCompaniesGetParameters } from '@api/model/managementSessionCompaniesGetParameters';
import { SearchManagementCompanyResponse } from '@api/model/searchManagementCompanyResponse';
import { ManagementApiService } from '@api/service/management.api.service';
import { UserService } from '@core/client/user.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FilterAPIField, FilterConfig } from '@shared/components/filter/filter.model';
import { FormControlSelectOption } from '@shared/components/form/form-control/form-control.model';
import { IIconCellRendererParams } from '@shared/components/list/ag-grid/cell-renderers/icon-cell-renderer.component';
import { IImageCellRendererParams, ImageCellRendererComponent } from '@shared/components/list/ag-grid/cell-renderers/image-cell-renderer.component';
import { INameCellRendererParams, NameCellRendererComponent } from '@shared/components/list/ag-grid/cell-renderers/name-cell-renderer.component';
import { StatusCellRendererComponent } from '@shared/components/list/ag-grid/cell-renderers/status-cell-renderer.component';
import { ITextCellRendererParams, TextCellRendererComponent } from '@shared/components/list/ag-grid/cell-renderers/text-cell-renderer.component';
import { EntityListComponent } from '@shared/components/list/ag-grid/entity-list.component';
import { IconHeaderRendererComponent } from '@shared/components/list/ag-grid/header-renderers/icon-header-renderer.component';
import { EntityListDirective } from '@shared/components/list/entity-list.directive';
import { CommonFunctions } from '@shared/services/common-functions.service';
import { ColDef } from 'node_modules/ag-grid-community/dist/types/src/main-umd-noStyles';
import { map, Observable } from 'rxjs';
import { CompanyNewComponent } from "../company-new/company-new.component";
import { CompanyListFeatureFlags } from './company-list.features';
import { CompanyItemTargetLocation } from './company-list.model';

@Component({
    selector: 'app-company-list',
    imports: [FontAwesomeModule, CompanyNewComponent, EntityListComponent],
    templateUrl: './company-list.component.html',
    styleUrl: './company-list.component.scss',
    host: {
        class: 'col-12 flex-grow-1 d-flex flex-column gap-3'
    }
})
export class CompanyListComponent extends EntityListDirective<CompanyListFeatureFlags, ManagementCompany, ManagementSessionCompaniesGetParameters|ManagementCompaniesGetParameters, SearchManagementCompanyResponse> {

    private userService = inject(UserService);
    private managementApiService = inject(ManagementApiService);
    private router = inject(Router);

    targetLocation = input<CompanyItemTargetLocation>('edit');

    override navigationUrlTree: WritableSignal<(data: ManagementCompany) => any[]> = signal((data:ManagementCompany) => {

        switch(this.targetLocation()){
            case 'home':
                return ['/company', data?.id];
            case 'stay':
                return [this.router.url.replace(/\/company\/\d+\//, `/company/${data?.id}/`)];
            case 'edit':
                return [data?.id];
            default:
                return [];
        }

    });
    
    override getData = input<
        ((params: Partial<ManagementSessionCompaniesGetParameters|ManagementCompaniesGetParameters>) => Observable<SearchManagementCompanyResponse | undefined>)| undefined,
        ((params: Partial<ManagementSessionCompaniesGetParameters|ManagementCompaniesGetParameters>) => Observable<SearchManagementCompanyResponse | undefined>)| undefined
    >(undefined, {
        alias: 'get-data',
        transform: (call) => call ? (params: Partial<ManagementSessionCompaniesGetParameters|ManagementCompaniesGetParameters>) => call({
            _includeSubData: true,
            _includeSubDataFilter: [
                CommonFunctions.getPropertyName<ManagementCompany>(company => company.files),
                CommonFunctions.getPropertyName<ManagementCompany>(company => company.organisation),
            ],
            ...params
        })
        : undefined
    });

    override COLUMN_DEFINITIONS: WritableSignal<ColDef<any, any>[]> = signal<ColDef[]>([
        {
            field: CommonFunctions.getPropertyPathJoined<ManagementOrganisation>(org => org.files),
            headerComponent: IconHeaderRendererComponent,
            headerComponentParams: <IIconCellRendererParams>{
                style: 'fad',
                icon: 'image-polaroid'
            },
            cellRenderer: ImageCellRendererComponent,
            cellRendererParams: <IImageCellRendererParams>{
                imageType: 'company',
                fileType: FileTypeEnum.ProfileImage
            },
            sortable: false,
            initialWidth: 125
        },
        {
            headerName: 'Name',
            field: CommonFunctions.getPropertyPathJoined<ManagementCompany>(company => company.fullName),
            cellRenderer: NameCellRendererComponent,
            cellRendererParams: <INameCellRendererParams>{
                additionalPaths: [
                    CommonFunctions.getPropertyPathJoined<ManagementOrganisation>(company => company.shortName)
                ]
            }
        },
        {
            headerName: 'Status',
            field: CommonFunctions.getPropertyPathJoined<ManagementCompany>(company => company.companyStatus),
            cellRenderer: StatusCellRendererComponent
        },
        {
            headerName: 'Organisation',
            field: CommonFunctions.getPropertyPathJoined<ManagementCompany>(company => company.organisation?.fullName),
            cellRenderer: TextCellRendererComponent
        },
        {
            headerName: 'Your License',
            field: CommonFunctions.getPropertyPathJoined<ManagementCompany>(company => company.id),
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: <ITextCellRendererParams>{
                preprocessing: (entity:ManagementCompany) => {
                    const licenseKeys = this.userService.user()?.licenseKeys;
                    return licenseKeys?.find((license) => license.company?.id == entity.id)?.licenseType?.name ?? '';
                },
            }
        }
    ]);

    filterOptions = computed<FilterConfig<ManagementSessionCompaniesGetParameters&ManagementCompaniesGetParameters>>(() => {
        return {
            groups: [
                {
                    name: 'Company',
                    filters: [
                        new FilterAPIField({
                            callProperty: 'filterByFullNames',
                            display: 'Full Name',
                            type: 'text'
                        }),
                        new FilterAPIField({
                            callProperty: 'filterByShortNames',
                            display: 'Short Name',
                            type: 'text'
                        }),
                        new FilterAPIField({
                            callProperty: 'filterByStatuses',
                            display: 'Status',
                            type: 'text'
                        }),
                        new FilterAPIField({
                            callProperty: 'filterByOrganisationIds',
                            display: 'Organisation',
                            type: 'multi-select',
                            options: {
                                select: {
                                    dataSource: () => this.managementApiService.Management.Session.Companies.Get({params:{_includeSubData:true}})
                                    .pipe(
                                        map((response) => response.result?.map((company) => company.organisation?.fullName) ?? []),
                                        map((names) => names?.filter((name) => name != undefined)),
                                        map((names) => [...new Set(names)]),
                                        map((name) => name?.map((organisation) => 
                                            <FormControlSelectOption>{
                                                option: organisation,
                                                value: organisation
                                            }
                                        ) ?? [])
                                    )
                                }
                            }
                        })
                    ]
                }
            ]
        }
    })
}
